import { StatCard } from '@thegpvc/ui';
import { Pulse } from './Pulse';
import { SectionCard } from './SectionCard';
import { buildPulse, hasPulse } from '../utils/pulse';
import { text, cx } from '../styles/theme';
import { formatDuration, formatNumber } from '../utils/format';
import type { SessionDetail, Sitting } from '../types/api';

/**
 * The Session Story hero viz (#489 Phase 2c): the full-session Pulse over a
 * vitals band of gp-ui StatCards. The Pulse renders straight from raw
 * per-sitting activity, so this surface works even when the LLM narrative is
 * missing. When no sitting has a Pulse series yet, it degrades to the vitals.
 */

function LegendSwatch({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cx('inline-block h-2.5 w-2.5 rounded-sm', className)} />
      <span className={cx('text-xs', text.secondary)}>{label}</span>
    </span>
  );
}

function sessionTokenTotal(session: SessionDetail): number {
  return (
    parseInt(session.inputTokens || '0', 10) +
    parseInt(session.outputTokens || '0', 10) +
    parseInt(session.cacheCreationTokens || '0', 10) +
    parseInt(session.cacheReadTokens || '0', 10)
  );
}

export function SessionPulseCard({
  session,
  sittings,
}: {
  session: SessionDetail;
  sittings: Sitting[];
}) {
  const pulse = buildPulse(sittings ?? []);
  const showPulse = hasPulse(pulse);

  const totalTokens = sessionTokenTotal(session);
  const prompts = session.genuinePromptCount || session.turnCount || 0;
  const autonomy = session.autonomyScore ? `${Math.round(session.autonomyScore * 100)}%` : '—';

  const legend = showPulse ? (
    <div className="flex items-center gap-3">
      <LegendSwatch className="bg-sky-500 dark:bg-sky-400" label="Output" />
      <LegendSwatch className="bg-violet-400 dark:bg-violet-400" label="Input" />
      <LegendSwatch className="bg-orange-500 dark:bg-orange-400" label="Prompts" />
    </div>
  ) : undefined;

  return (
    <SectionCard title="Activity" action={legend} bodyClassName="space-y-4">
      <>
        {showPulse ? (
          // The horizontal scrollbar renders inside this overflow container, so
          // it would otherwise overlay the bottom of the bars and prompt pips.
          // `pb-3` reserves a band below the graph for the scrollbar to sit in,
          // and `-mb-3` reclaims that band from the surrounding `space-y-4` gap
          // so the overall spacing stays pixel-identical (no extra padding).
          <div className="overflow-x-auto pb-3 -mb-3" data-testid="pulse-scroll">
            <Pulse data={pulse} variant="full" ariaLabel="Full session activity pulse" />
          </div>
        ) : (
          <p className={cx('text-sm', text.secondary)}>
            No activity pulse yet for this session — run{' '}
            <span className={text.primary}>Reprocess All</span> in Settings to generate it.
          </p>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard surface="plain" variant="compact" label="Tokens" value={totalTokens > 0 ? formatNumber(totalTokens) : '—'} />
          <StatCard surface="plain" variant="compact" label="Tools" value={formatNumber(session.toolCallCount || 0)} />
          <StatCard surface="plain" variant="compact" label="Prompts" value={formatNumber(prompts)} />
          <StatCard surface="plain" variant="compact" label="AI turns" value={formatNumber(session.aiTurnCount || 0)} />
          <StatCard surface="plain" variant="compact" label="Active" value={session.durationMs ? formatDuration(session.durationMs) : '—'} />
          <StatCard surface="plain" variant="compact" color="accent" label="Autonomy" value={autonomy} />
        </div>
      </>
    </SectionCard>
  );
}
